import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Container, Form, ListGroup, Modal, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Pessoa from '../../../model/Pessoa';
import MembroBanca, { PapelNome, StatusMembroNome } from '../../../model/banca/MembroBanca';
import { useBreadcrumbs } from '../../../Routes/BreadcrumbContext';
import { createBreadcrumbs } from '../../../utils/breadcrumbUtils';
import { ComposicaoDisponibilidade, ComposicaoTemplates, PapelComposicao, VinculoQuantidade } from '../../../model/banca/ComposicaoDisponibilidade';
import { ComposicaoDisponibilidadeViewModel } from '../../../viewmodel/banca/ComposicaoDisponibilidadeViewModel';
import TemplateSelectorDefinir from './components/TemplateSelectorDefinir';
import { MembrosBancaViewModel } from '../../../viewmodel/banca/MembrosBancaViewModel';
import { normalizarComposicao } from '../../../utils/papelUtils';
import { BancaViewModel } from '../../../viewmodel/banca/BancaViewModel';
import { BancaUtils, TipoBancaNome } from '../../../model/banca/Banca';
import { CategoriaEdital, formatarCategoria } from '../../../model/processo/Categoria';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import ModernStepper, { StepperNavigation } from './components/ModernStepper';
import ModalSelecionarPresidente from './components/ModalSelecionarPresidente';

interface SelecoesEstado {
    titulares: Record<string, MembroBanca[]>;
    papeis: Record<string, MembroBanca[]>;
}

interface Step {
    id: string;
    label: string;
}

const STEPS: Step[] = [
    { id: 'TITULAR', label: 'Titulares' },
    { id: 'TECNICO', label: 'Técnicos(as)' },
    { id: 'SECRETARIO', label: 'Secretários(as)' },
    { id: 'SUPLENTE', label: 'Suplentes' },
    { id: 'CONCLUSAO', label: 'Conclusão' }
];

const INT_MAX = 2147483647;

const sanitizeMax = (valor: number): number => (valor >= INT_MAX ? Number.POSITIVE_INFINITY : valor);

const formatLimite = (minimo: number, maximo: number): string => {
    const max = sanitizeMax(maximo);
    if (max === Number.POSITIVE_INFINITY) {
        return minimo === 0 ? 'Opcional' : `Min ${minimo}`;
    }
    return minimo === max ? `${max}` : `${minimo} - ${max}`;
};

const papelDisplay = (papel: string): string => {
    switch (papel) {
        case 'TITULAR':
            return 'Titulares';
        case 'TECNICO':
            return 'Técnicos';
        case 'SECRETARIO':
            return 'Secretários';
        case 'SUPLENTE':
            return 'Suplentes';
        default:
            return papel;
    }
};

const papelEnumMap: Record<string, PapelNome> = {
    TITULAR: PapelNome.TITULAR,
    TECNICO: PapelNome.TECNICO,
    SECRETARIO: PapelNome.SECRETARIO,
    SUPLENTE: PapelNome.SUPLENTE
};

const papelNomeToKey = (nome: PapelNome): string => {
    switch (nome) {
        case PapelNome.TITULAR:
            return 'TITULAR';
        case PapelNome.TECNICO:
            return 'TECNICO';
        case PapelNome.SECRETARIO:
            return 'SECRETARIO';
        case PapelNome.SUPLENTE:
            return 'SUPLENTE';
        default:
            return 'Papel Desconhecido';
    }
};

const normalizar = (valor: string) => valor.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const getBadgeVariant = (quantidade: number, minimo: number, maximo: number): string => {
    if (quantidade < minimo) {
        return 'secondary';
    }
    const limite = sanitizeMax(maximo);
    if (limite !== Number.POSITIVE_INFINITY && quantidade > limite) {
        return 'danger';
    }
    return 'success';
};

const DefinirMembrosBanca: React.FC = () => {
    const { processoId, bancaId } = useParams<{ processoId: string; bancaId: string }>();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumbs();

    const [loading, setLoading] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [pessoas, setPessoas] = useState<Pessoa[]>([]);
    const [membros, setMembros] = useState<MembroBanca[]>([]);
    const [composicaoTemplates, setComposicaoTemplates] = useState<ComposicaoTemplates | null>(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [composicao, setComposicao] = useState<ComposicaoDisponibilidade | null>(null);
    const [selecoes, setSelecoes] = useState<SelecoesEstado>({ titulares: {}, papeis: {} });
    const [busca, setBusca] = useState('');
    const [filtroVinculo, setFiltroVinculo] = useState<string>('TODOS');
    const [preenchidoSelecoes, setPreenchidoSelecoes] = useState(false);
    const [stepAtual, setStepAtual] = useState<string>(STEPS[0].id);
    const [showModalPresidente, setShowModalPresidente] = useState(false);
    const [presidenteId, setPresidenteId] = useState<string | null>(null);
    const [showModalAjuda, setShowModalAjuda] = useState(false);

    useEffect(() => {
        if (!processoId || !bancaId) {
            return;
        }
        setBreadcrumbs(createBreadcrumbs.fromProcessos([
            { path: `/processos/${processoId}/bancas`, breadcrumb: 'Bancas' },
            { path: `/processos/${processoId}/bancas/${bancaId}/definir-membros`, breadcrumb: 'Definir membros' }
        ]));
    }, [setBreadcrumbs, processoId, bancaId]);

    useEffect(() => {
        const carregar = async () => {
            if (!processoId || !bancaId) {
                return;
            }
            try {
                setLoading(true);
                const membrosCompletos = await MembrosBancaViewModel.getMembrosCompletos(processoId, bancaId, true);

                const candidatos = membrosCompletos.filter(membro =>
                    membro.statusMembro === StatusMembroNome.CONFIRMADO ||
                    membro.statusMembro === StatusMembroNome.DEFINIDO
                );

                setMembros(candidatos);
                setPessoas(Array.from(new Map(candidatos.map(m => [m.pessoa.id, m.pessoa])).values()));

                // Carregar templates de composição
                try {
                    const composicaoResponse = await ComposicaoDisponibilidadeViewModel.getComposicaoDefinicao(processoId, bancaId);
                    setComposicaoTemplates(composicaoResponse);
                    
                    if (composicaoResponse.templates.length === 1) {
                        const template = composicaoResponse.templates[0];
                        setSelectedTemplateId(template.id);
                        setComposicao(normalizarComposicao({
                            tipoBanca: composicaoResponse.tipoBanca,
                            categoriaEdital: composicaoResponse.categoriaEdital,
                            papeis: template.papeis
                        }));
                    }
                } catch (compError) {
                    console.error('Erro ao carregar composição:', compError);
                }
            } catch (error) {
                toast.error(BackendErrorMessage(error));
            } finally {
                setLoading(false);
            }
        };

        carregar();
    }, [processoId, bancaId]);

    useEffect(() => {
        if (!composicaoTemplates || !selectedTemplateId) return;
        
        const template = composicaoTemplates.templates.find(t => t.id === selectedTemplateId);
        if (template) {
            setComposicao(normalizarComposicao({
                tipoBanca: composicaoTemplates.tipoBanca,
                categoriaEdital: composicaoTemplates.categoriaEdital,
                papeis: template.papeis
            }));
            setSelecoes({ titulares: {}, papeis: {} });
            setPreenchidoSelecoes(false);
        }
    }, [composicaoTemplates, selectedTemplateId]);

    const handleTemplateSelect = (templateId: string) => {
        setSelectedTemplateId(templateId);
    };

    useEffect(() => {
        if (!composicao) {
            return;
        }
        setSelecoes(prev => {
            const titularesBase: Record<string, MembroBanca[]> = {};
            const titularConfig = composicao.papeis.find(p => p.papel === 'TITULAR');
            if (titularConfig) {
                titularConfig.vinculos?.forEach(vinculo => {
                    titularesBase[vinculo.vinculo] = prev.titulares[vinculo.vinculo] ?? [];
                });
            }

            const papeisBase: Record<string, MembroBanca[]> = {};
            composicao.papeis
                .filter(p => p.papel !== 'TITULAR')
                .forEach(papel => {
                    papeisBase[papel.papel] = prev.papeis[papel.papel] ?? [];
                });

            return { titulares: titularesBase, papeis: papeisBase };
        });
        setPreenchidoSelecoes(false);
    }, [composicao]);

    useEffect(() => {
        if (!composicao || preenchidoSelecoes) {
            return;
        }
        const titularConfig = composicao.papeis.find(p => p.papel === 'TITULAR');
        if (!titularConfig) {
            setPreenchidoSelecoes(true);
            return;
        }

        setSelecoes(prev => {
            const titulares: Record<string, MembroBanca[]> = { ...prev.titulares };
            titularConfig.vinculos?.forEach(vinculo => {
                titulares[vinculo.vinculo] = titulares[vinculo.vinculo] ?? [];
            });
            const papeis: Record<string, MembroBanca[]> = { ...prev.papeis };
            composicao.papeis.filter(p => p.papel !== 'TITULAR').forEach(papel => {
                papeis[papel.papel] = papeis[papel.papel] ?? [];
            });

            membros
                .filter(membro => membro.statusMembro === StatusMembroNome.DEFINIDO)
                .forEach(membro => {
                    const papelKey = papelNomeToKey(membro.papelMembro.nome);
                    if (papelKey === 'TITULAR') {
                        const vinculo = membro.pessoa.vinculo.nome;
                        if (titulares[vinculo] && !titulares[vinculo].some(item => item.id === membro.id)) {
                            titulares[vinculo] = [...titulares[vinculo], membro];
                        }
                    } else if (papeis[papelKey] && !papeis[papelKey].some(item => item.id === membro.id)) {
                        papeis[papelKey] = [...papeis[papelKey], membro];
                    }
                });

            return { titulares, papeis };
        });

        setPreenchidoSelecoes(true);
    }, [composicao, membros, preenchidoSelecoes]);

    const titularConfig = useMemo(() => composicao?.papeis.find(p => p.papel === 'TITULAR') ?? null, [composicao]);
    const outrosPapeis = useMemo(() => {
        if (!composicao) {
            return [] as PapelComposicao[];
        }
        const ordem = { TECNICO: 1, SECRETARIO: 2, SUPLENTE: 3 } as Record<string, number>;
        return [...composicao.papeis.filter(p => p.papel !== 'TITULAR')].sort((a, b) => {
            const left = ordem[a.papel] ?? 99;
            const right = ordem[b.papel] ?? 99;
            return left - right;
        });
    }, [composicao]);

    const vinculosDisponiveis = useMemo(() => {
        const valores = Array.from(new Set(pessoas.map(p => p.vinculo.nome)));
        valores.sort((a, b) => a.localeCompare(b));
        return valores;
    }, [pessoas]);

    const membrosPorPapel = useMemo(() => {
        const mapa = new Map<string, MembroBanca[]>();
        membros.forEach(membro => {
            const key = membro.papelMembro.nome; // Usar o nome exato do papel
            const lista = mapa.get(key) ?? [];
            mapa.set(key, [...lista, membro]);
        });
        return mapa;
    }, [membros]);

    const pessoaSelecionadaEmOutroTitular = useCallback((membro: MembroBanca, vinculoAtual: string) => {
        return Object.entries(selecoes.titulares).some(([vinculo, lista]) =>
            vinculo !== vinculoAtual && lista.some(item => item.pessoa.id === membro.pessoa.id)
        );
    }, [selecoes.titulares]);

    // CORREÇÃO: Mantém todos os membros sempre visíveis, apenas aplica filtros de busca/vinculo
    const aplicaFiltros = useCallback((lista: MembroBanca[], contextoVinculo?: string): MembroBanca[] => {
        return lista.filter(membro => {
            // Filtro por vínculo
            if (filtroVinculo !== 'TODOS') {
                if (contextoVinculo) {
                    if (filtroVinculo !== contextoVinculo) {
                        return false;
                    }
                } else if (membro.pessoa.vinculo.nome !== filtroVinculo) {
                    return false;
                }
            }
            
            // Filtro por busca
            const comparador = normalizar(busca);
            if (!comparador) {
                return true;
            }
            const nome = normalizar(membro.pessoa.nome ?? '');
            const cpf = (membro.pessoa.cpf ?? '').replace(/\D/g, '');
            const matricula = (membro.pessoa.matricula ?? '').toLowerCase();
            return nome.includes(comparador) || cpf.includes(comparador) || matricula.includes(comparador);
        });
    }, [busca, filtroVinculo]);

    const titularesDisponiveis = useMemo(() => {
        if (!titularConfig) {
            return {} as Record<string, MembroBanca[]>;
        }
        const resultado: Record<string, MembroBanca[]> = {};
        titularConfig.vinculos?.forEach(vinculo => {
            const candidatos = (membrosPorPapel.get('Titular') ?? [])
                .filter(membro => membro.pessoa.vinculo.nome === vinculo.vinculo)
                .filter(membro => !pessoaSelecionadaEmOutroTitular(membro, vinculo.vinculo));
            
            resultado[vinculo.vinculo] = aplicaFiltros(candidatos, vinculo.vinculo);
        });
        return resultado;
    }, [aplicaFiltros, membrosPorPapel, pessoaSelecionadaEmOutroTitular, titularConfig]);

    const papeisDisponiveis = useMemo(() => {
        return outrosPapeis.map(papel => {
            // Mapear os nomes dos papéis para os valores do backend
            const papelNomeBackend = papel.papel === 'TECNICO' ? 'Técnico' : 
                                   papel.papel === 'SECRETARIO' ? 'Secretário' : 
                                   papel.papel === 'SUPLENTE' ? 'Suplente' : papel.papel;

            const candidatos = membrosPorPapel.get(papelNomeBackend) ?? [];
            const vinculosPermitidos = papel.vinculos?.map(v => v.vinculo) ?? [];
            const filtrados = vinculosPermitidos.length > 0
                ? candidatos.filter(membro => vinculosPermitidos.includes(membro.pessoa.vinculo.nome))
                : candidatos;
            
            return {
                papel,
                lista: aplicaFiltros(filtrados),
                selecionados: selecoes.papeis[papel.papel] ?? []
            };
        });
    }, [aplicaFiltros, membrosPorPapel, outrosPapeis, selecoes.papeis]);

    const toggleTitular = (vinculo: VinculoQuantidade, membro: MembroBanca) => {
        const isTecnicoOuSecretario = membro.papelMembro.nome === PapelNome.TECNICO || membro.papelMembro.nome === PapelNome.SECRETARIO;
        if (!isTecnicoOuSecretario && membro.pessoa.jaDefinidoMesmoEditalChamada) {
            toast.warning('Este membro já foi definido em outra banca do mesmo edital e chamada.');
            return;
        }
        
        setSelecoes(prev => {
            const listaAtual = prev.titulares[vinculo.vinculo] ?? [];
            const jaSelecionado = listaAtual.some(item => item.id === membro.id);
            if (jaSelecionado) {
                return {
                    titulares: {
                        ...prev.titulares,
                        [vinculo.vinculo]: listaAtual.filter(item => item.id !== membro.id)
                    },
                    papeis: prev.papeis
                };
            }

            const limite = sanitizeMax(vinculo.maximo);
            if (limite !== Number.POSITIVE_INFINITY && listaAtual.length >= limite) {
                toast.warning(`Limite de ${limite} titular(es) para ${vinculo.vinculo} atingido.`);
                return prev;
            }

            return {
                titulares: {
                    ...prev.titulares,
                    [vinculo.vinculo]: [...listaAtual, membro]
                },
                papeis: prev.papeis
            };
        });
    };

    const togglePapel = (papel: PapelComposicao, membro: MembroBanca) => {
        const isTecnicoOuSecretario = membro.papelMembro.nome === PapelNome.TECNICO || membro.papelMembro.nome === PapelNome.SECRETARIO;
        if (!isTecnicoOuSecretario && membro.pessoa.jaDefinidoMesmoEditalChamada) {
            toast.warning('Este membro já foi definido em outra banca do mesmo edital e chamada.');
            return;
        }
        
        setSelecoes(prev => {
            const listaAtual = prev.papeis[papel.papel] ?? [];
            const jaSelecionado = listaAtual.some(item => item.id === membro.id);
            if (jaSelecionado) {
                return {
                    titulares: prev.titulares,
                    papeis: {
                        ...prev.papeis,
                        [papel.papel]: listaAtual.filter(item => item.id !== membro.id)
                    }
                };
            }

            const limite = sanitizeMax(papel.maximo);
            if (limite !== Number.POSITIVE_INFINITY && listaAtual.length >= limite) {
                toast.warning(`Limite de ${limite} ${papelDisplay(papel.papel).toLowerCase()} atingido.`);
                return prev;
            }

            return {
                titulares: prev.titulares,
                papeis: {
                    ...prev.papeis,
                    [papel.papel]: [...listaAtual, membro]
                }
            };
        });
    };

    const validarSelecoes = (): boolean => {
        if (!composicao) {
            toast.error('Composição nao carregada.');
            return false;
        }

        for (const papel of composicao.papeis) {
            if (papel.papel === 'TITULAR') {
                const selecionadosPorVinculo = selecoes.titulares;
                for (const vinculo of (papel.vinculos ?? [])) {
                    const quantidade = selecionadosPorVinculo[vinculo.vinculo]?.length ?? 0;
                    if (quantidade < vinculo.minimo) {
                        toast.error(`Selecione ${vinculo.minimo} titular(es) para ${vinculo.vinculo}.`);
                        return false;
                    }
                    const limite = sanitizeMax(vinculo.maximo);
                    if (limite !== Number.POSITIVE_INFINITY && quantidade > limite) {
                        toast.error(`Maximo de ${limite} titular(es) para ${vinculo.vinculo}.`);
                        return false;
                    }
                }
            } else {
                const quantidade = selecoes.papeis[papel.papel]?.length ?? 0;
                if (quantidade < papel.minimo) {
                    toast.error(`Selecione pelo menos ${papel.minimo} ${papelDisplay(papel.papel).toLowerCase()}.`);
                    return false;
                }
                const limite = sanitizeMax(papel.maximo);
                if (limite !== Number.POSITIVE_INFINITY && quantidade > limite) {
                    toast.error(`Maximo de ${limite} ${papelDisplay(papel.papel).toLowerCase()}.`);
                    return false;
                }
            }
        }
        return true;
    };

    const handleAbrirModalPresidente = () => {
        if (!processoId || !bancaId || !composicao) {
            toast.error('Informacoes insuficientes para salvar a banca.');
            return;
        }

        if (!validarSelecoes()) {
            return;
        }

        // Coletar todos os membros selecionados, excluindo Técnicos e Secretários
        const todosMembros: MembroBanca[] = [
            ...Object.values(selecoes.titulares).flat(),
            ...Object.values(selecoes.papeis).flat()
        ].filter(membro => 
            membro.papelMembro.nome !== PapelNome.TECNICO && 
            membro.papelMembro.nome !== PapelNome.SECRETARIO
        );

        // Pré-selecionar Docente Titular se existir
        const docenteTitular = todosMembros.find(
            m => m.pessoa.vinculo.nome === 'Docente' && m.papelMembro.nome === PapelNome.TITULAR
        );
        
        if (docenteTitular) {
            setPresidenteId(docenteTitular.id);
        } else {
            setPresidenteId(null);
        }

        setShowModalPresidente(true);
    };

    const handleSalvar = async () => {

        if (!presidenteId) {
            toast.error('Selecione um presidente para a banca.');
            return;
        }

        setSalvando(true);
        setShowModalPresidente(false);
        
        try {
            const membrosParaEnvio: MembroBanca[] = [];

            Object.entries(selecoes.titulares).forEach(([, lista]) => {
                lista.forEach(membro => {
                    membrosParaEnvio.push({
                        ...membro,
                        statusMembro: StatusMembroNome.DEFINIDO,
                        isPresidente: membro.id === presidenteId
                    });
                });
            });

            Object.entries(selecoes.papeis).forEach(([papel, lista]) => {
                lista.forEach(membro => {
                    membrosParaEnvio.push({
                        ...membro,
                        papelMembro: {
                            ...membro.papelMembro,
                            nome: papelEnumMap[papel]
                        },
                        statusMembro: StatusMembroNome.DEFINIDO,
                        isPresidente: membro.id === presidenteId
                    });
                });
            });

            if (membrosParaEnvio.length === 0) {
                toast.error('Selecione pelo menos um membro para definir a banca.');
                setSalvando(false);
                return;
            }

            await BancaViewModel.definirBanca(processoId!, bancaId!, membrosParaEnvio);
            toast.success('Banca definida com sucesso.');
            navigate(`/processos/${processoId}/bancas`);
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        } finally {
            setSalvando(false);
        }
    };

    const isMembroSelecionado = useCallback((colecao: MembroBanca[], membro: MembroBanca) =>
        colecao.some(item => item.id === membro.id), []);

    const etapasConcluidas = useMemo(() => {
        const concluídas = new Set<string>();
        if (!composicao) {
            return concluídas;
        }

        composicao.papeis.forEach(papel => {
            if (papel.papel === 'TITULAR') {
                const selecionadosPorVinculo = selecoes.titulares;
                const todasValidas = (papel.vinculos ?? []).every(vinculo => {
                    const quantidade = selecionadosPorVinculo[vinculo.vinculo]?.length ?? 0;
                    return quantidade >= vinculo.minimo;
                });
                if (todasValidas) {
                    concluídas.add('TITULAR');
                }
            } else {
                const quantidade = selecoes.papeis[papel.papel]?.length ?? 0;
                if (quantidade >= papel.minimo) {
                    concluídas.add(papel.papel);
                }
            }
        });

        return concluídas;
    }, [composicao, selecoes]);

    const renderMembroCard = (membro: MembroBanca, selecionado: boolean, onToggle: (m: MembroBanca) => void) => {
        const jaDefinido = membro.pessoa.jaDefinidoMesmoEditalChamada;
        const jaConsultado = membro.pessoa.jaConsultadoMesmoEditalChamada;
        const isTecnicoOuSecretario = membro.papelMembro.nome === PapelNome.TECNICO || membro.papelMembro.nome === PapelNome.SECRETARIO;
        const isDesabilitado = !isTecnicoOuSecretario && jaDefinido;
        
        return (
            <Card
                key={membro.id ?? membro.pessoa.id}
                className={`mb-2 border-${selecionado ? 'primary' : 'light'} ${selecionado ? 'shadow' : ''} ${isDesabilitado ? 'bg-light' : ''}`}
                style={{ 
                    cursor: isDesabilitado ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    borderWidth: selecionado ? '2px' : '1px',
                    borderLeft: selecionado ? '4px solid #0d6efd' : 
                               jaDefinido ? '4px solid #dc3545' :
                               jaConsultado ? '4px solid #ffc107' : 
                               '4px solid transparent',
                    opacity: isDesabilitado ? 0.6 : 1
                }}
                onClick={() => !isDesabilitado && onToggle(membro)}
            >
                <Card.Body className="py-2">
                    <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                            <div className="fw-semibold d-flex align-items-center gap-2">
                                {membro.pessoa.nome}
                                {jaDefinido && (
                                    <Badge 
                                        bg="danger" 
                                        className="ms-2"
                                        title="Já foi definido como membro no mesmo Edital e Chamada, em outra banca"
                                    >
                                        Já Definido
                                    </Badge>
                                )}
                                {jaConsultado && !jaDefinido && (
                                    <Badge 
                                        bg="warning" 
                                        text="dark" 
                                        className="ms-2"
                                        title="Já foi consultado anteriormente no mesmo Edital e Chamada, em outra banca"
                                    >
                                        Já Consultado
                                    </Badge>
                                )}
                                {!membro.pessoa.isMembroCavu && (
                                    <Badge bg="secondary" text="white" title="Pessoa externa à instituição">Externo</Badge>
                                )}
                            </div>
                            <small className="text-muted">
                                {membro.pessoa.vinculo.nome}
                                {membro.pessoa.matricula ? ` · ${membro.pessoa.matricula}` : ''}
                            </small>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            {selecionado && !isDesabilitado && (
                                <div className="d-flex align-items-center">
                                    <div 
                                        className="bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                        style={{ width: '20px', height: '20px' }}
                                    >
                                        <i className="bi bi-check text-white" style={{ fontSize: '0.7rem' }} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    const renderTitulares = () => {
        if (!titularConfig) {
            return null;
        }
        return (
            <Row className="g-3">
                {(titularConfig.vinculos ?? [])
                    .sort((a, b) => {
                        if (a.vinculo.toLowerCase().includes('docente')) return -1;
                        if (b.vinculo.toLowerCase().includes('docente')) return 1;
                        return 0;
                    })
                    .map(vinculo => {
                    const selecionados = selecoes.titulares[vinculo.vinculo] ?? [];
                    const candidatos = titularesDisponiveis[vinculo.vinculo] ?? [];
                    
                    return (
                        <Col key={vinculo.vinculo} md={6} xl={4}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="fw-semibold">{vinculo.vinculo}</span>
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip id={`tt-${vinculo.vinculo}`}>Limite {formatLimite(vinculo.minimo, vinculo.maximo)}</Tooltip>}
                                        >
                                            <Badge bg={getBadgeVariant(selecionados.length, vinculo.minimo, vinculo.maximo)}>
                                                {selecionados.length} / {formatLimite(vinculo.minimo, vinculo.maximo)}
                                            </Badge>
                                        </OverlayTrigger>
                                    </div>
                                </Card.Header>
                                <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {candidatos.length === 0 ? (
                                        <div className="text-muted fst-italic text-center py-3">
                                            Nenhum membro disponível
                                        </div>
                                    ) : (
                                        candidatos.map(membro => renderMembroCard(
                                            membro,
                                            isMembroSelecionado(selecionados, membro),
                                            candidato => toggleTitular(vinculo, candidato)
                                        ))
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
        );
    };

    const renderPapel = (config: PapelComposicao) => {
        const selecionados = selecoes.papeis[config.papel] ?? [];
        const papelDisponivel = papeisDisponiveis.find(item => item.papel.papel === config.papel);
        const candidatos = papelDisponivel?.lista ?? [];
        
        return (
            <Row className="g-3">
                {config.vinculos && config.vinculos.length > 0 ? (
                    config.vinculos.map(vinculo => (
                        <Col key={`${config.papel}-${vinculo.vinculo}`} md={6} xl={4}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="fw-semibold">{vinculo.vinculo}</span>
                                        <Badge bg={getBadgeVariant(
                                            selecionados.filter(item => item.pessoa.vinculo.nome === vinculo.vinculo).length,
                                            vinculo.minimo,
                                            vinculo.maximo
                                        )}>
                                            {formatLimite(vinculo.minimo, vinculo.maximo)}
                                        </Badge>
                                    </div>
                                </Card.Header>
                                <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {candidatos
                                        .filter(membro => membro.pessoa.vinculo.nome === vinculo.vinculo)
                                        .map(membro => renderMembroCard(
                                            membro,
                                            isMembroSelecionado(selecionados, membro),
                                            candidato => togglePapel(config, candidato)
                                        ))}
                                    {candidatos.filter(membro => membro.pessoa.vinculo.nome === vinculo.vinculo).length === 0 && (
                                        <div className="text-muted fst-italic text-center py-3">
                                            Nenhum membro disponível
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    ))
                ) : (
                    <Col md={6} xl={4}>
                        <Card className="h-100 border-0 shadow-sm">
                            <Card.Header className="bg-light">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="fw-semibold">{papelDisplay(config.papel)}</span>
                                    <Badge bg={getBadgeVariant(selecionados.length, config.minimo, config.maximo)}>
                                        {selecionados.length} / {formatLimite(config.minimo, config.maximo)}
                                    </Badge>
                                </div>
                            </Card.Header>
                            <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {candidatos.map(membro => renderMembroCard(
                                    membro,
                                    isMembroSelecionado(selecionados, membro),
                                    candidato => togglePapel(config, candidato)
                                ))}
                                {candidatos.length === 0 && (
                                    <div className="text-muted fst-italic text-center py-3">
                                        Nenhum membro disponível
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                )}
            </Row>
        );
    };

    const renderConclusao = () => {
        const selecionadosTotais = [
            ...Object.values(selecoes.titulares).flat(),
            ...Object.values(selecoes.papeis).flat()
        ];

        return (
            <div className="d-flex flex-column gap-3">
                <Card className="shadow-sm">
                    <Card.Header className="bg-light">
                        <h5 className="mb-0">Resumo da Banca - {selecionadosTotais.length} membros selecionados</h5>
                    </Card.Header>
                    <Card.Body>
                        {selecionadosTotais.length === 0 ? (
                            <div className="text-muted fst-italic text-center py-4">
                                Nenhum membro selecionado
                            </div>
                        ) : (
                            <Row className="g-3">
                                {/* Titulares por vínculo */}
                                {titularConfig && Object.entries(selecoes.titulares).map(([vinculo, membros]) => {
                                    if (membros.length === 0) return null;
                                    return (
                                        <Col key={vinculo} md={6} lg={4}>
                                            <Card className="h-100 border-primary">
                                                <Card.Header className="bg-primary text-white">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="fw-semibold">Titulares - {vinculo}</span>
                                                        <Badge bg="light" text="dark">{membros.length}</Badge>
                                                    </div>
                                                </Card.Header>
                                                <Card.Body className="p-2">
                                                    <ListGroup variant="flush">
                                                        {membros.map(membro => (
                                                            <ListGroup.Item key={membro.id} className="px-2 py-1 border-0">
                                                                <div className="fw-semibold">{membro.pessoa.nome}</div>
                                                                <small className="text-muted">
                                                                    {membro.pessoa.matricula ? `${membro.pessoa.matricula} · ` : ''}
                                                                    {membro.pessoa.vinculo.nome}
                                                                </small>
                                                            </ListGroup.Item>
                                                        ))}
                                                    </ListGroup>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    );
                                })}
                                
                                {/* Outros papéis */}
                                {Object.entries(selecoes.papeis).map(([papel, membros]) => {
                                    if (membros.length === 0) return null;
                                    const papelConfig = composicao?.papeis.find(p => p.papel === papel);
                                    const bgColor = papel === 'TECNICO' ? 'success' : papel === 'SECRETARIO' ? 'info' : 'warning';
                                    
                                    return (
                                        <Col key={papel} md={6} lg={4}>
                                            <Card className={`h-100 border-${bgColor}`}>
                                                <Card.Header className={`bg-${bgColor} text-white`}>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="fw-semibold">{papelDisplay(papel)}</span>
                                                        <Badge bg="light" text="dark">{membros.length}</Badge>
                                                    </div>
                                                </Card.Header>
                                                <Card.Body className="p-2">
                                                    <ListGroup variant="flush">
                                                        {membros.map(membro => (
                                                            <ListGroup.Item key={membro.id} className="px-2 py-1 border-0">
                                                                <div className="fw-semibold">{membro.pessoa.nome}</div>
                                                                <small className="text-muted">
                                                                    {membro.pessoa.matricula ? `${membro.pessoa.matricula} · ` : ''}
                                                                    {membro.pessoa.vinculo.nome}
                                                                </small>
                                                            </ListGroup.Item>
                                                        ))}
                                                    </ListGroup>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        )}
                    </Card.Body>
                </Card>
                
                {/* Resumo de validação */}
                <Card className="shadow-sm">
                    <Card.Header className="bg-light">
                        <h6 className="mb-0">Status de Validação</h6>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            {composicao?.papeis.map(papel => {
                                const quantidade = papel.papel === 'TITULAR'
                                    ? Object.values(selecoes.titulares).reduce((acc, lista) => acc + lista.length, 0)
                                    : selecoes.papeis[papel.papel]?.length ?? 0;
                                const isValido = quantidade >= papel.minimo;
                                return (
                                    <Col key={papel.papel} md={6} lg={3} className="mb-2">
                                        <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                                            <span>{papelDisplay(papel.papel)}</span>
                                            <div className="d-flex align-items-center gap-2">
                                                <Badge bg={getBadgeVariant(quantidade, papel.minimo, papel.maximo)}>
                                                    {quantidade} / {formatLimite(papel.minimo, papel.maximo)}
                                                </Badge>
                                                {isValido ? (
                                                    <i className="bi bi-check-circle-fill text-success" />
                                                ) : (
                                                    <i className="bi bi-exclamation-circle-fill text-warning" />
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Card.Body>
                </Card>
            </div>
        );
    };

    const renderConteudoEtapa = () => {
        if (!composicao) {
            return null;
        }
        if (stepAtual === 'CONCLUSAO') {
            return renderConclusao();
        }
        if (stepAtual === 'TITULAR') {
            return renderTitulares();
        }
        const papelConfig = composicao.papeis.find(p => p.papel === stepAtual);
        if (!papelConfig) {
            return null;
        }
        return renderPapel(papelConfig);
    };

    const selecionadosTotais = useMemo(() => {
        const titulares = Object.values(selecoes.titulares).flat();
        const demais = Object.values(selecoes.papeis).flat();
        return [...titulares, ...demais];
    }, [selecoes]);

    const handleProximo = () => {
        const currentIndex = STEPS.findIndex(step => step.id === stepAtual);
        if (currentIndex < STEPS.length - 1) {
            setStepAtual(STEPS[currentIndex + 1].id);
        }
    };

    const handleAnterior = () => {
        const currentIndex = STEPS.findIndex(step => step.id === stepAtual);
        if (currentIndex > 0) {
            setStepAtual(STEPS[currentIndex - 1].id);
        }
    };

    const isEtapaValida = useMemo(() => {
        if (!composicao) return false;
        
        if (stepAtual === 'CONCLUSAO') {
            return validarSelecoes();
        }
        
        const papelConfig = composicao.papeis.find(p => p.papel === stepAtual);
        if (!papelConfig) return false;

        if (papelConfig.papel === 'TITULAR') {
            const selecionadosPorVinculo = selecoes.titulares;
            return (papelConfig.vinculos ?? []).every(vinculo => {
                const quantidade = selecionadosPorVinculo[vinculo.vinculo]?.length ?? 0;
                return quantidade >= vinculo.minimo;
            });
        } else {
            const quantidade = selecoes.papeis[papelConfig.papel]?.length ?? 0;
            return quantidade >= papelConfig.minimo;
        }
    }, [composicao, stepAtual, selecoes]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <Spinner animation="border" />
            </div>
        );
    }

    if (!composicaoTemplates) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <div className="text-center">
                    <h5>Erro ao carregar templates</h5>
                    <p className="text-muted mb-3">Não foi possível carregar os templates de composição.</p>
                </div>
            </div>
        );
    }

    if (composicaoTemplates.templates.length > 1 && !selectedTemplateId) {
        return (
            <Container fluid className="py-4">
                <TemplateSelectorDefinir
                    templates={composicaoTemplates.templates}
                    selectedTemplateId={selectedTemplateId}
                    onTemplateSelect={handleTemplateSelect}
                />
            </Container>
        );
    }

    if (!composicao) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <Spinner animation="border" />
            </div>
        );
    }

    const tipoBancaNome = composicao.tipoBanca?.nome as TipoBancaNome | undefined;
    const categoria = composicao.categoriaEdital as CategoriaEdital;

    return (
        <Container fluid className="py-4" style={{ position: 'relative' }}>
            <Button
                variant="link"
                className="text-muted p-1"
                style={{ 
                    position: 'absolute',
                    top: '-48px',
                    right: '20px',
                    fontSize: '1.2rem',
                    textDecoration: 'none',
                    zIndex: 1000
                }}
                onClick={() => setShowModalAjuda(true)}
                title="Ajuda"
            >
                <i className="bi bi-question-circle" />
            </Button>

            {composicaoTemplates.templates.length > 1 && (
                <TemplateSelectorDefinir
                    templates={composicaoTemplates.templates}
                    selectedTemplateId={selectedTemplateId}
                    onTemplateSelect={handleTemplateSelect}
                />
            )}

            <ModernStepper
                steps={STEPS}
                current={stepAtual}
                onSelect={setStepAtual}
                completed={etapasConcluidas}
                className="mb-4"
            />

            <Row className="g-3 align-items-end mb-4">
                <Col md={6} lg={4}>
                    <Form.Group controlId="busca-definir">
                        <Form.Label>Buscar por nome, CPF ou matrícula</Form.Label>
                        <Form.Control
                            type="text"
                            value={busca}
                            placeholder="Digite para filtrar"
                            onChange={event => setBusca(event.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={6} lg={3}>
                    <Form.Group controlId="filtroVinculo-definir">
                        <Form.Label>Filtrar por vínculo</Form.Label>
                        <Form.Select value={filtroVinculo} onChange={event => setFiltroVinculo(event.target.value)}>
                            <option value="TODOS">Todos os vínculos</option>
                            {vinculosDisponiveis.map(vinculo => (
                                <option key={vinculo} value={vinculo}>{vinculo}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={12} lg={5}>
                    <Card className="shadow-sm">
                        <Card.Body className="d-flex justify-content-between align-items-center">
                            <div>
                                <div className="fw-semibold">Definir membros da banca</div>
                                <div className="text-muted">
                                    Tipo: <strong>{tipoBancaNome ?? 'Nao informado'}</strong>
                                    {' · '}Categoria: <strong>{formatarCategoria(categoria)}</strong>
                                </div>
                            </div>
                            <div style={{ fontSize: '32px' }}>
                                {tipoBancaNome ? BancaUtils.getIconeBanca(tipoBancaNome) : null}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="g-3 mb-4">
                <Col xl={9}>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-light">
                            <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">{stepAtual === 'CONCLUSAO' ? 'Conclusão' : papelDisplay(stepAtual)}</h6>
                                {stepAtual !== 'CONCLUSAO' && (
                                    <Badge bg="primary">
                                        {stepAtual === 'TITULAR' 
                                            ? Object.values(selecoes.titulares).reduce((acc, lista) => acc + lista.length, 0)
                                            : selecoes.papeis[stepAtual]?.length ?? 0
                                        } selecionados
                                    </Badge>
                                )}
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {renderConteudoEtapa()}
                        </Card.Body>
                    </Card>
                </Col>
                <Col xl={3}>
                    <Card className="shadow-sm h-100">
                        <Card.Header className="fw-semibold">Resumo</Card.Header>
                        <Card.Body>
                            <div className="d-flex justify-content-between mb-3">
                                <span>Total selecionados</span>
                                <Badge bg={selecionadosTotais.length > 0 ? 'success' : 'secondary'}>
                                    {selecionadosTotais.length}
                                </Badge>
                            </div>
                            {STEPS.map(step => {
                                const quantidade = step.id === 'TITULAR'
                                    ? Object.values(selecoes.titulares).reduce((acc, lista) => acc + lista.length, 0)
                                    : selecoes.papeis[step.id]?.length ?? 0;
                                const papelConfig = composicao.papeis.find(p => p.papel === step.id);
                                const minimo = papelConfig?.minimo ?? 0;
                                const badgeVariant = quantidade >= minimo ? 'success' : 'secondary';
                                return (
                                    <div key={step.id} className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="small">{step.label}</span>
                                        <Badge bg={badgeVariant}>{quantidade}</Badge>
                                    </div>
                                );
                            })}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <StepperNavigation
                currentStep={stepAtual}
                steps={STEPS}
                onPrevious={handleAnterior}
                onNext={handleProximo}
                onSubmit={handleAbrirModalPresidente}
                isSubmitting={salvando}
                isValid={isEtapaValida}
                showSubmit={stepAtual === STEPS[STEPS.length - 1].id}
            />

            <ModalSelecionarPresidente
                show={showModalPresidente}
                membros={[
                    ...Object.values(selecoes.titulares).flat(),
                    ...Object.values(selecoes.papeis).flat()
                ]}
                presidenteId={presidenteId}
                onSelect={setPresidenteId}
                onConfirm={handleSalvar}
                onCancel={() => setShowModalPresidente(false)}
            />

            <Modal show={showModalAjuda} onHide={() => setShowModalAjuda(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Ajuda - Definir Membros da Banca</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Esta página é usada para selecionar os membros definitivos da banca.
                    </p>
                    <p>
                        Ao finalizar o procedimento a Banca muda de Status para DEFINIDA.
                    </p>
                    <p className="mb-2">
                        <strong>Critérios de seleção:</strong>
                    </p>
                    <ul className="mb-0">
                        <li>Apenas pessoas que confirmaram disponibilidade para a data desta banca aparecerão como opção</li>
                        <li>Não serão exibidas pessoas que já fazem parte de bancas de outros tipos no mesmo processo</li>
                        <li>Papeis e Vínculos dos selecionados devem obedecer a Composição de Banca definida previamente</li>
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModalAjuda(false)}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default DefinirMembrosBanca;